import { create } from 'zustand'
import type { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'

// Generic Supabase session gate. RequireAuth + LoginPage consume this. The
// source project layered an approval-status / profile check on top; that's
// app-specific and was dropped from the baseline. Add it back in a separate
// store (or extend this one) if your app needs gated access.

interface AuthState {
  user: User | null
  session: Session | null
  loading: boolean
  initialized: boolean
  error: string | null

  initialize: () => Promise<void>
  signInWithEmail: (email: string, password: string) => Promise<void>
  signUpWithEmail: (email: string, password: string) => Promise<boolean>
  requestPasswordReset: (email: string) => Promise<void>
  signOut: () => Promise<void>
  clearError: () => void
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: null,
  session: null,
  loading: false,
  initialized: false,
  error: null,

  initialize: async () => {
    // Idempotent: only the first call wires up the session + listener.
    if (get().initialized) return

    set({ loading: true })

    try {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession()

      if (error) throw error

      set({
        session,
        user: session?.user ?? null,
        initialized: true,
        loading: false,
      })

      // Keep store in sync with Supabase auth changes (token refresh, sign-out
      // in another tab, OAuth redirect completion, etc.).
      supabase.auth.onAuthStateChange((_event, session) => {
        set({
          session,
          user: session?.user ?? null,
        })
      })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to initialize auth',
        loading: false,
        initialized: true,
      })
    }
  },

  signInWithEmail: async (email: string, password: string) => {
    set({ loading: true, error: null })

    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) throw error
      set({ loading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to sign in',
        loading: false,
      })
    }
  },

  signUpWithEmail: async (email: string, password: string): Promise<boolean> => {
    set({ loading: true, error: null })

    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        },
      })
      if (error) throw error
      set({ loading: false })
      return true
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to sign up',
        loading: false,
      })
      return false
    }
  },

  requestPasswordReset: async (email: string) => {
    set({ loading: true, error: null })

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/login`,
      })
      if (error) throw error
      set({ loading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to send reset email',
        loading: false,
      })
    }
  },

  signOut: async () => {
    set({ loading: true, error: null })

    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error

      set({ user: null, session: null, loading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to sign out',
        loading: false,
      })
    }
  },

  clearError: () => set({ error: null }),
}))
